import { useState, useRef } from 'react'
import { X, Download, Upload, AlertCircle, CheckCircle, Loader } from 'lucide-react'

const PRODUCTS = ['AGO', 'PMS', 'DPK']
const REQUIRED_COLS = ['transport_company', 'driver_name', 'product', 'litres', 'unit_price', 'destination', 'sale_date']

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/)
  if (lines.length < 2) return { rows: [], error: 'File is empty or has no data rows.' }
  const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, '').toLowerCase().replace(/\s+/g, '_'))
  const missing = REQUIRED_COLS.filter((c) => !headers.includes(c))
  if (missing.length) return { rows: [], error: `Missing required columns: ${missing.join(', ')}` }
  const rows = lines.slice(1).filter((l) => l.trim()).map((line, i) => {
    const values = []
    let cur = '', inQ = false
    for (const ch of line) {
      if (ch === '"') inQ = !inQ
      else if (ch === ',' && !inQ) { values.push(cur.trim()); cur = '' }
      else cur += ch
    }
    values.push(cur.trim())
    const row = {}
    headers.forEach((h, j) => { row[h] = values[j]?.replace(/^"|"$/g, '') || '' })
    row._rowNum = i + 2
    return row
  })
  return { rows, error: null }
}

function validateRows(rows, branchId, companies, drivers, prices) {
  return rows.map((row) => {
    const errors = []
    const warnings = []
    if (!row.transport_company) errors.push('Missing company name')
    if (!row.driver_name) errors.push('Missing driver name')
    if (!row.destination) errors.push('Missing destination')
    const product = row.product?.trim().toUpperCase()
    if (!PRODUCTS.includes(product)) errors.push('Product must be AGO, PMS, or DPK')
    const litres = Number(row.litres)
    if (!litres || litres <= 0) errors.push('Litres must be a positive number')
    const unitPrice = Number(row.unit_price)
    if (!unitPrice || unitPrice <= 0) errors.push('unit_price must be a positive number (e.g. 1050)')
    const dateVal = row.sale_date ? new Date(row.sale_date) : null
    if (!dateVal || isNaN(dateVal.getTime())) errors.push('Invalid date — use YYYY-MM-DD')
    const company = branchId
      ? companies.find((c) => c.branchId === branchId && c.companyName.toLowerCase() === row.transport_company?.toLowerCase().trim() && c.active)
      : null
    if (row.transport_company && !company) errors.push(`Company "${row.transport_company}" not found on this branch`)
    const driver = drivers.find((d) => d.fullName.toLowerCase() === row.driver_name?.toLowerCase().trim())
    if (row.driver_name && !driver) warnings.push(`"${row.driver_name}" not found — will be created as new driver`)
    return { ...row, product, litres, unitPrice, _errors: errors, _warnings: warnings, _valid: errors.length === 0, _company: company, _driver: driver }
  })
}

function downloadTemplate() {
  const today = new Date().toISOString().slice(0, 10)
  const rows = [
    'transport_company,driver_name,licence_plate,product,litres,unit_price,destination,sale_date,notes',
    `ABC Logistics Ltd,Emeka Okonkwo,ABC-123-XY,AGO,5000,1050,Kano,${today},`,
    `XYZ Transport Co,Chidi Nwosu,DEF-456-AB,PMS,3000,870,Lagos,${today},Urgent delivery`,
  ]
  const blob = new Blob([rows.join('\n')], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = 'CreditSales_Template.csv'; a.click()
  URL.revokeObjectURL(url)
}

export default function CreditBulkUploadModal({ currentUser, branches, companies, drivers, prices, onImport, onClose }) {
  const [branchId, setBranchId] = useState(currentUser.branchId || '')
  const [step, setStep] = useState('upload')
  const [fileName, setFileName] = useState('')
  const [parsedRows, setParsedRows] = useState([])
  const [validatedRows, setValidatedRows] = useState([])
  const [parseError, setParseError] = useState('')
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const [importResult, setImportResult] = useState(null)
  const fileRef = useRef(null)

  const isAdmin = currentUser.role !== 'manager'
  const visibleBranches = currentUser.role === 'manager' ? branches.filter((b) => b.id === currentUser.branchId) : branches

  function handleBranchChange(id) {
    setBranchId(id)
    if (parsedRows.length > 0) setValidatedRows(validateRows(parsedRows, id, companies, drivers, prices))
  }

  function handleFileChange(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setFileName(file.name)
    setParseError('')
    const reader = new FileReader()
    reader.onload = (ev) => {
      const { rows, error } = parseCSV(ev.target.result)
      if (error) { setParseError(error); setParsedRows([]); return }
      const validated = validateRows(rows, branchId, companies, drivers, prices)
      setParsedRows(rows)
      setValidatedRows(validated)
      setStep('preview')
    }
    reader.readAsText(file)
  }

  async function handleImport() {
    const valid = validatedRows.filter((r) => r._valid)
    setProgress({ done: 0, total: valid.length })
    setStep('importing')
    const result = await onImport(valid, branchId, (done) => setProgress((p) => ({ ...p, done })))
    setImportResult(result)
    setStep('done')
  }

  function reset() { setStep('upload'); setParsedRows([]); setValidatedRows([]); setFileName(''); setParseError('') }

  const validCount = validatedRows.filter((r) => r._valid).length
  const errorCount = validatedRows.filter((r) => !r._valid).length
  const warnCount = validatedRows.filter((r) => r._valid && r._warnings.length > 0).length

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/50 animate-fadeIn" onClick={step === 'importing' ? undefined : onClose} />
      <div className="fixed inset-x-0 bottom-0 z-[70] animate-slideUp flex flex-col bg-white rounded-t-2xl shadow-2xl" style={{ maxHeight: '92vh' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-100 flex-shrink-0">
          <div>
            <h3 className="font-bold text-gray-800">Bulk Import Sales</h3>
            <p className="text-xs text-gray-400">Upload a CSV to import multiple sales at once</p>
          </div>
          {step !== 'importing' && (
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">

          {/* ── Upload step ── */}
          {step === 'upload' && (
            <>
              {isAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Branch *</label>
                  <select value={branchId} onChange={(e) => handleBranchChange(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none">
                    <option value="">Select a branch…</option>
                    {visibleBranches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm font-semibold text-blue-800 mb-1">Step 1 — Download the template</p>
                <p className="text-xs text-blue-600 mb-3">Fill it in with your sales data, then upload below.</p>
                <button onClick={downloadTemplate}
                  className="flex items-center space-x-2 bg-blue-600 text-white text-sm font-semibold px-4 py-2 rounded-xl">
                  <Download className="w-4 h-4" /><span>Download Template CSV</span>
                </button>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
                <p className="text-sm font-semibold text-gray-700 mb-1">Step 2 — Upload filled CSV</p>
                <p className="text-xs text-gray-400 mb-4">Company names must match exactly what's in the Companies list.</p>
                <button onClick={() => fileRef.current?.click()} disabled={isAdmin && !branchId}
                  className="flex items-center space-x-2 mx-auto bg-gray-800 disabled:opacity-40 text-white text-sm font-semibold px-4 py-2.5 rounded-xl">
                  <Upload className="w-4 h-4" /><span>Choose CSV File</span>
                </button>
                <input ref={fileRef} type="file" accept=".csv" onChange={handleFileChange} className="hidden" />
                {isAdmin && !branchId && <p className="text-xs text-amber-600 mt-2">Select a branch first</p>}
                {fileName && <p className="text-xs text-gray-500 mt-2">{fileName}</p>}
              </div>

              {parseError && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  <p className="text-sm text-red-700">{parseError}</p>
                </div>
              )}

              <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Required columns</p>
                {[
                  ['transport_company', 'Exact company name from Companies list'],
                  ['driver_name', 'Full name (auto-created if new)'],
                  ['product', 'AGO, PMS, or DPK'],
                  ['litres', 'Positive number e.g. 5000'],
                  ['unit_price', 'Price per litre e.g. 1050'],
                  ['destination', 'e.g. Kano, Lagos'],
                  ['sale_date', 'Format: YYYY-MM-DD'],
                ].map(([col, desc]) => (
                  <div key={col} className="flex items-start space-x-2">
                    <code className="text-xs bg-white border border-gray-200 rounded px-1.5 py-0.5 text-blue-700 flex-shrink-0">{col}</code>
                    <span className="text-xs text-gray-500">{desc}</span>
                  </div>
                ))}
                <p className="text-xs text-gray-400 pt-1">Optional: <code className="bg-white border border-gray-200 rounded px-1">licence_plate</code>, <code className="bg-white border border-gray-200 rounded px-1">notes</code></p>
              </div>
            </>
          )}

          {/* ── Preview step ── */}
          {step === 'preview' && (
            <>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
                  <p className="text-lg font-bold text-green-700">{validCount}</p>
                  <p className="text-xs text-green-600">Valid</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
                  <p className="text-lg font-bold text-amber-700">{warnCount}</p>
                  <p className="text-xs text-amber-600">Warnings</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
                  <p className="text-lg font-bold text-red-700">{errorCount}</p>
                  <p className="text-xs text-red-600">Errors</p>
                </div>
              </div>

              {errorCount > 0 && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                  {errorCount} row{errorCount !== 1 ? 's have' : ' has'} errors and will be skipped. You can still import the {validCount} valid rows.
                </p>
              )}

              <div className="space-y-2">
                {validatedRows.map((row) => (
                  <div key={row._rowNum} className={`rounded-xl border p-3 ${
                    !row._valid ? 'border-red-200 bg-red-50' :
                    row._warnings.length > 0 ? 'border-amber-200 bg-amber-50' :
                    'border-gray-100 bg-white'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center space-x-1.5">
                          <span className="text-xs text-gray-400 font-mono flex-shrink-0">#{row._rowNum}</span>
                          <p className="text-sm font-semibold text-gray-800 truncate">{row.transport_company || '—'}</p>
                        </div>
                        <p className="text-xs text-gray-500">{row.driver_name} · {row.product} · {row.litres} L @ ₦{row.unit_price}/L · {row.destination}</p>
                        <p className="text-xs text-gray-400">{row.sale_date}</p>
                      </div>
                      <div className="ml-2 flex-shrink-0">
                        {row._valid
                          ? <CheckCircle className="w-4 h-4 text-green-500" />
                          : <AlertCircle className="w-4 h-4 text-red-500" />
                        }
                      </div>
                    </div>
                    {row._errors.map((e, i) => <p key={i} className="text-xs text-red-600 mt-1">✗ {e}</p>)}
                    {row._warnings.map((w, i) => <p key={i} className="text-xs text-amber-600 mt-1">⚠ {w}</p>)}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── Importing step ── */}
          {step === 'importing' && (
            <div className="py-10 text-center space-y-5">
              <Loader className="w-10 h-10 text-blue-600 animate-spin mx-auto" />
              <div>
                <p className="font-semibold text-gray-800">Importing sales…</p>
                <p className="text-sm text-gray-400 mt-1">{progress.done} of {progress.total}</p>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2 mx-auto max-w-xs">
                <div className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: progress.total ? `${(progress.done / progress.total) * 100}%` : '0%' }} />
              </div>
            </div>
          )}

          {/* ── Done step ── */}
          {step === 'done' && importResult && (
            <div className="space-y-4 py-2">
              <div className="bg-green-50 border border-green-200 rounded-xl p-5 text-center">
                <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
                <p className="font-bold text-green-800 text-lg">{importResult.success} sale{importResult.success !== 1 ? 's' : ''} imported</p>
                {importResult.failed > 0 && <p className="text-sm text-red-600 mt-1">{importResult.failed} row{importResult.failed !== 1 ? 's' : ''} failed</p>}
              </div>
              {importResult.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 space-y-1">
                  <p className="text-xs font-semibold text-red-700 mb-1">Failed rows:</p>
                  {importResult.errors.map((e, i) => <p key={i} className="text-xs text-red-600">{e}</p>)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-4 pt-3 pb-6 border-t border-gray-100">
          {step === 'upload' && (
            <button onClick={onClose} className="w-full py-3 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm">Cancel</button>
          )}
          {step === 'preview' && (
            <div className="flex space-x-3">
              <button onClick={reset} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm">Re-upload</button>
              <button onClick={handleImport} disabled={validCount === 0}
                className="flex-1 py-3 rounded-xl bg-blue-600 disabled:opacity-40 text-white font-semibold text-sm">
                Import {validCount} Sale{validCount !== 1 ? 's' : ''}
              </button>
            </div>
          )}
          {step === 'done' && (
            <button onClick={onClose} className="w-full py-3 rounded-xl bg-blue-600 text-white font-semibold text-sm">Done — View Ledger</button>
          )}
        </div>
      </div>
    </>
  )
}
